/**
 * OPFS Manager — Origin Private File System persistence layer.
 *
 * Saves one JSON file per trip inside the browser's private file system.
 * OPFS is separate from HTTP cache — it survives "Clear browsing data" and
 * normal cache purges on Chrome, Firefox and Safari (iOS 15.2+).
 * Data is only removed by "Clear site data" or deleting the PWA on iOS.
 *
 * Photos are NOT stored here (too large). Only trip metadata + activity data.
 * If IndexedDB is wiped, this layer restores everything except photos.
 */

const DIR = { root: 'wayfare', trips: 'trips' };

class OPFSManager {
  constructor() {
    this._supported = null;
    this._tripsDir = null;
  }

  isSupported() {
    if (this._supported === null) {
      this._supported =
        typeof navigator !== 'undefined' &&
        'storage' in navigator &&
        typeof navigator.storage?.getDirectory === 'function';
    }
    return this._supported;
  }

  async _dir() {
    if (this._tripsDir) return this._tripsDir;
    const root    = await navigator.storage.getDirectory();
    const wayfare = await root.getDirectoryHandle(DIR.root,  { create: true });
    const trips   = await wayfare.getDirectoryHandle(DIR.trips, { create: true });
    this._tripsDir = trips;
    return trips;
  }

  // ── Write ──────────────────────────────────────────────────────────────────

  async saveTrip(tripData) {
    if (!this.isSupported() || !tripData?.tripId) return false;
    try {
      const dir = await this._dir();
      const fh  = await dir.getFileHandle(`${tripData.tripId}.json`, { create: true });
      const w   = await fh.createWritable();
      await w.write(JSON.stringify({
        tripId:           tripData.tripId,
        destination:      tripData.destination,
        dates:            tripData.dates,
        status:           tripData.status,
        stats:            tripData.stats,
        notes:            tripData.notes            || [],
        reflections:      tripData.reflections      || [],
        challenges:       tripData.challenges       || [],
        luggage:          tripData.luggage          || [],
        groceries:        tripData.groceries        || [],
        gifts:            tripData.gifts            || [],
        lodgings:         tripData.lodgings         || [],
        privateDocuments: tripData.privateDocuments || [],
        travelInfo:       tripData.travelInfo       || {},
        createdAt:        tripData.createdAt,
        lastModified:     tripData.lastModified     || new Date().toISOString(),
        _opfsSavedAt:     new Date().toISOString(),
      }));
      await w.close();
      this._emit('saved', tripData.tripId);
      return true;
    } catch (e) {
      console.warn('[OPFS] saveTrip failed:', e.message);
      return false;
    }
  }

  async deleteTrip(tripId) {
    if (!this.isSupported()) return false;
    try {
      const dir = await this._dir();
      await dir.removeEntry(`${tripId}.json`);
      return true;
    } catch (e) {
      return false;
    }
  }

  // ── Read ───────────────────────────────────────────────────────────────────

  async getTrip(tripId) {
    if (!this.isSupported()) return null;
    try {
      const dir  = await this._dir();
      const fh   = await dir.getFileHandle(`${tripId}.json`);
      const file = await fh.getFile();
      return JSON.parse(await file.text());
    } catch (e) {
      return null;
    }
  }

  async getAllTrips() {
    if (!this.isSupported()) return [];
    try {
      const dir   = await this._dir();
      const trips = [];
      for await (const [name, handle] of dir.entries()) {
        if (name.endsWith('.json') && handle.kind === 'file') {
          try {
            const file = await handle.getFile();
            const data = JSON.parse(await file.text());
            if (data?.tripId) trips.push(data);
          } catch (_) { /* skip corrupt file */ }
        }
      }
      return trips;
    } catch (e) {
      return [];
    }
  }

  async hasTrips() {
    if (!this.isSupported()) return false;
    try {
      const dir = await this._dir();
      for await (const _ of dir.entries()) return true;
      return false;
    } catch (_) {
      return false;
    }
  }

  // ── Internal ───────────────────────────────────────────────────────────────

  _emit(status, tripId) {
    window.dispatchEvent(new CustomEvent('wayfare-opfs-status', {
      detail: { status, tripId, ts: Date.now() },
    }));
  }
}

const opfsManager = new OPFSManager();
export default opfsManager;
